import React, { useEffect, useState } from 'react';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Button } from '@uhg-abyss/web/ui/Button';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { ToastProvider } from '@uhg-abyss/web/ui/Toast';
import { SuggestProgramType } from '@src/api.types';

export const STATES =
  'AL|AK|AS|AZ|AR|CA|CO|CT|DE|DC|FM|FL|GA|GU|HI|ID|IL|IN|IA|KS|KY|LA|ME|MH|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|MP|OH|OK|OR|PW|PA|PR|RI|SC|SD|TN|TX|UT|VT|VI|VA|WA|WV|WI|WY';

export type SuggestProgramEditModalProps = {
  programData: SuggestProgramType;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSaveProgram: (program: SuggestProgramType) => void;
  listOfValidFields: string[];
};

export const SuggestProgramEditModal = ({
  programData,
  isOpen,
  setIsOpen,
  onSaveProgram,
  listOfValidFields = [],
}: SuggestProgramEditModalProps) => {
  const [isFormValid, setIsFormValid] = useState(false);
  const [validFields, setValidFields] = useState<string[]>(listOfValidFields);

  const form = useForm({
    defaultValues: {
      providerName: programData?.providerCBOName ?? '',
      programName: programData?.programName ?? '',
      programPhone: programData?.programPhone ?? '',
      programStateCode: programData?.stateCode ?? '',
      programWebsite: programData?.programWebsite ?? '',
      programNotes: programData?.notes ?? '',
      emailAddress: programData?.advocateEmail ?? '',
    },
  });

  useEffect(() => {
    setIsFormValid(validFields.length === 4);
  }, [validFields]);

  const removeFromValidFields = (field: string) => {
    setValidFields((l) => l.filter((f) => f !== field));
  };

  const addToValidFields = (field: string) => {
    if (!validFields.includes(field)) {
      setValidFields([...validFields, field]);
    }
  };

  type FormFieldName = 'providerName' | 'programName' | 'programPhone' | 'programStateCode' | 'programWebsite' | 'programNotes' | 'emailAddress';

  const validateProviderAndProgram = (field: FormFieldName, value: string | null, message: string) => {
    if (!value) {
      form.setError(field, { type: 'manual', message });
      removeFromValidFields(field);
    } else {
      form.clearErrors(field);
      addToValidFields(field);
    }
  };

  const validatePhoneOrWebsite = (phone: string | null | undefined, website: string | null | undefined) => {
    const phoneRegex = /^\(?\d{3}\)?[-.●]?\d{3}[-.●]?\d{4}$/;
    const websiteRegex =
      /^(https?:\/\/|www\.)[\da-z.-]+\.[a-z]{2,6}(\/[\w.-]*)*\/?$/i;
    if (!phone && !website) {
      form.setError('programPhone', { type: 'manual', message: 'Program phone or program website is required' });
      form.setError('programWebsite', { type: 'manual', message: 'Program phone or program website is required' });
      removeFromValidFields('phoneWebsite');
    } else if (phone && !phoneRegex.test(phone)) {
      form.setError('programPhone', { type: 'manual', message: 'Invalid phone number format' });
      removeFromValidFields('phoneWebsite');
    } else if (website && !websiteRegex.test(website)) {
      form.setError('programWebsite', { type: 'manual', message: 'Invalid website URL format' });
      removeFromValidFields('phoneWebsite');
    } else {
      form.clearErrors('programPhone');
      form.clearErrors('programWebsite');
      addToValidFields('phoneWebsite');
    }
  };

  const validateStateCode = (stateCode: string | null) => {
    if (stateCode && stateCode.length === 2 && STATES.indexOf(stateCode) > 0) {
      form.clearErrors('programStateCode');
      addToValidFields('stateCode');
    } else {
      form.setError('programStateCode', { type: 'manual', message: 'State code is a required field' });
      removeFromValidFields('stateCode');
    }
  };

  function isEmpty(obj: any) {
    for (const prop in obj) {
      if (Object.hasOwn(obj, prop)) {
        return false;
      }
    }
    return true;
  }

  const handleSaveProgram = () => {
    const data = form.getValues();
    const savedProgram: SuggestProgramType = {
      providerCBOName: data.providerName ?? '',
      programName: data.programName ?? '',
      programPhone: data.programPhone ?? '',
      programWebsite: data.programWebsite,
      stateCode: data.programStateCode ?? '',
      notes: data.programNotes,
      advocateEmail: data.emailAddress,
    };
    onSaveProgram(savedProgram);
  };

  const style = {
    paddingTop: '15px',
    paddingBottom: '24px',
    minHeight: '140px',
  };

  return (
    <div>
      <ModalDialog
        closeOnClickOutside={false}
        size="$md"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <div data-cy="suggest-program-edit-modal">
          <div>
            <Heading level={5}>Suggest a program instructions</Heading>
            <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
              <Layout.Group
                alignLayout="center"
                style={{
                  marginTop: '5px',
                  marginBottom: '5px',
                  marginLeft: '5px',
                  padding: '15px',
                  border: 'solid #D3D3D3 1px',
                  borderRadius: '10px',
                }}
              >
                <FormProvider state={form}>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        label="Provider/CBO Name (required)"
                        model="providerName"
                        isClearable
                        onClear={() => {
                          validateProviderAndProgram('providerName', null, 'Provider name is required');
                        }}
                        onChange={(e) => {
                          validateProviderAndProgram('providerName', e.target.value, 'Provider name is required');
                        }}
                        validators={{ required: 'Please enter provider/CBO name' }}
                        subText="This is the name of the organization that provides the service (e.g. The Salvation Army)"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        label="Program Name and Description (required)"
                        model="programName"
                        onChange={(e) => {
                          validateProviderAndProgram('programName', e.target.value, 'Program name is required');
                        }}
                        isClearable
                        onClear={() => {
                          validateProviderAndProgram('programName', null, 'Program name is required');
                        }}
                        validators={{ required: 'Please enter program name/description' }}
                        subText="This is the name of the program or service (e.g. Salvation Army Food Pantry)"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        label="Program Phone (required if no program website)"
                        model="programPhone"
                        isClearable
                        onClear={() => {
                          validatePhoneOrWebsite(null, form.getValues()?.programWebsite);
                        }}
                        onChange={() => {
                          validatePhoneOrWebsite(form.getValues()?.programPhone, form.getValues()?.programWebsite);
                        }}
                        subText="This is the phone number of the program, please include area code"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        label="Program Website (required if no program phone number)"
                        model="programWebsite"
                        isClearable
                        onClear={() => {
                          validatePhoneOrWebsite(form.getValues()?.programPhone, null);
                        }}
                        onChange={() => {
                          validatePhoneOrWebsite(form.getValues()?.programPhone, form.getValues()?.programWebsite);
                        }}
                        subText="Only one URL per row. Please include www or http"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', paddingBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label>
                        <strong>State Code (required) </strong>
                        <span style={{ color: 'red' }}>*</span>
                      </label>
                      <TextInput
                        label=""
                        model="programStateCode"
                        isClearable
                        onClear={() => { validateStateCode(null); }}
                        onChange={(e) => { validateStateCode(e.target.value); }}
                        subText="Must be the abbreviation of the state the provider or program is in (e.g. TX)"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextInputArea label="Notes" model="programNotes" css={{ 'abyss-text-input-area': { fontFamily: 'inherit' } }} />
                    </div>
                  </div>
                  <div style={{ paddingBottom: '8px' }}>
                    <TextInput
                      label="Submitter email (required)"
                      model="emailAddress"
                      validators={{
                        required: 'Enter a valid email address',
                        pattern: {
                          value:
                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                          message: 'Invalid Email',
                        },
                      }}
                      subText="This is the email of the person submitting the request"
                    />
                  </div>
                </FormProvider>
              </Layout.Group>
              <Button
                data-cy="save"
                type="submit"
                isDisabled={!isFormValid || !isEmpty(form.formState.errors)}
                style={{ width: 'auto', marginLeft: '15px', whiteSpace: 'nowrap' }}
                onClick={() => {
                  setIsOpen(false);
                  handleSaveProgram();
                }}
              >
                Add program
              </Button>
            </div>
          </div>
        </div>
      </ModalDialog>
      <ToastProvider children={undefined} />
    </div>
  );
};
